import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { alpha } from "@mui/material/styles";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import LocalShippingOutlined from "@mui/icons-material/LocalShippingOutlined";
import { errorMessageOf, FURGONETKA_OAUTH_STORAGE_KEY, useFurgonetkaAccount } from "../../../../api/shipping";
import { getTenantId } from "../../../../api/utils";
import { CountrySelect } from "../../../../components/ui/countrySelect/countrySelect.component";
import { DetailPage, DetailSection } from "../../../../components/ui/detailView";
import { DECIMAL_INPUT_REGEX, formatDateTimeInline, toApiDecimal } from "../../../../utils";
import { ERROR_SNACKBAR, SUCCESS_SNACKBAR } from "../../../../consts";
import { useHasPermission } from "../../../../contexts/auth";
import { FurgonetkaAccount, FurgonetkaSettings } from "../../../../types/shipping";

const SETTINGS_FIELDS: (keyof FurgonetkaSettings)[] = [
  "sender_name",
  "sender_company",
  "sender_street",
  "sender_postcode",
  "sender_city",
  "sender_country",
  "sender_phone",
  "sender_email",
  "default_width",
  "default_depth",
  "default_height",
  "default_weight",
  "label_page_format",
];

const pickSettings = (account: FurgonetkaAccount): Record<string, string> =>
  Object.fromEntries(SETTINGS_FIELDS.map((field) => [field, String(account[field] ?? "")]));

/** Settings > Integrations: the workspace's own Furgonetka account, through
 * which parts are sent by courier. */
export const FurgonetkaIntegration = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const canChange = useHasPermission("config.change_tenantsettings");
  const { getStatus, getAuthorizeUrl, disconnect, updateSettings, loading } = useFurgonetkaAccount();

  const [account, setAccount] = useState<FurgonetkaAccount | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const apply = useCallback((data: FurgonetkaAccount) => {
    setAccount(data);
    setDraft(pickSettings(data));
  }, []);

  useEffect(() => {
    const load = async () => {
      const { data, success } = await getStatus();
      if (success) {
        apply(data);
      } else {
        enqueueSnackbar(t("Could not load the Furgonetka settings."), ERROR_SNACKBAR);
      }
    };
    load();
  }, [getStatus, apply, enqueueSnackbar, t]);

  const hasChanges = useMemo(
    () => Boolean(account) && SETTINGS_FIELDS.some((field) => draft[field] !== String(account?.[field] ?? "")),
    [account, draft]
  );

  const handleConnect = async () => {
    const { data, success, errorData } = await getAuthorizeUrl();
    if (!success) {
      enqueueSnackbar(errorMessageOf(errorData, t("Could not start connecting to Furgonetka.")), ERROR_SNACKBAR);
      return;
    }

    const state = new URL(data.url).searchParams.get("state");
    try {
      sessionStorage.setItem(FURGONETKA_OAUTH_STORAGE_KEY, JSON.stringify({ state, tenantId: getTenantId() }));
    } catch {
      // Without storage the callback falls back to the current workspace.
    }
    window.location.assign(data.url);
  };

  const handleDisconnect = async () => {
    if (!window.confirm(t("Disconnect the Furgonetka account? Shipments already sent keep their data."))) return;

    const { data, success, errorData } = await disconnect();
    if (success) {
      apply(data);
      enqueueSnackbar(t("Furgonetka account disconnected."), SUCCESS_SNACKBAR);
    } else {
      enqueueSnackbar(errorMessageOf(errorData, t("Could not disconnect the account.")), ERROR_SNACKBAR);
    }
  };

  const handleSave = async () => {
    const payload: Record<string, string> = { ...draft };
    ["default_weight"].forEach((field) => {
      payload[field] = toApiDecimal(payload[field]);
    });
    const { data, success, errorData } = await updateSettings(payload as any);
    if (success) {
      apply(data);
      setErrors({});
      enqueueSnackbar(t("Furgonetka settings saved."), SUCCESS_SNACKBAR);
    } else {
      const fieldErrors: Record<string, string> = {};
      Object.entries(errorData || {}).forEach(([field, value]: [string, any]) => {
        if (Array.isArray(value) && value[0]?.message) fieldErrors[field] = value[0].message;
      });
      setErrors(fieldErrors);
      enqueueSnackbar(errorMessageOf(errorData, t("Could not save the Furgonetka settings.")), ERROR_SNACKBAR);
    }
  };

  if (!account) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const field = (name: string, label: string, props: Record<string, any> = {}) => (
    <TextField
      size="small"
      label={label}
      value={draft[name] ?? ""}
      onChange={(event) => {
        const value = event.target.value;
        if (props.numeric && !DECIMAL_INPUT_REGEX.test(value)) return;
        setDraft((previous) => ({ ...previous, [name]: value }));
      }}
      disabled={!canChange || loading}
      error={Boolean(errors[name])}
      helperText={errors[name] || props.helperText}
      fullWidth
      required={props.required}
      type={props.type}
    />
  );

  const connected = account.connected;
  const needsReconnect = account.status === "needs_reconnect";

  return (
    <DetailPage>
      <Typography variant="body2" color="text.secondary">
        {t(
          "Connect your own Furgonetka.pl account to send parts from stock by courier. Shipments are ordered and paid on that account - with Furgonetka's prices or your own carrier contracts."
        )}
      </Typography>

      {!account.available && (
        <Alert severity="info">{t("Courier shipments are not available in this workspace.")}</Alert>
      )}

      {account.available && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
            px: 2,
            py: 1.5,
            borderRadius: 1,
            border: "1px solid",
            borderColor: (theme) =>
              connected ? alpha(theme.palette.success.main, 0.4) : theme.palette.divider,
            bgcolor: (theme) => (connected ? alpha(theme.palette.success.main, 0.08) : theme.palette.action.hover),
          }}
        >
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
            <LocalShippingOutlined color={connected ? "success" : "disabled"} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Furgonetka.pl{account.sandbox ? ` (${t("sandbox")})` : ""}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {connected
                  ? t("Connected {{date}} by {{name}}", {
                      date: formatDateTimeInline(account.connected_at),
                      name: account.connected_by_name || "-",
                    })
                  : needsReconnect
                  ? t("Furgonetka no longer accepts the connection - connect again.")
                  : t("Not connected")}
              </Typography>
            </Box>
          </Box>
          {canChange && (
            <Box sx={{ display: "flex", gap: 1 }}>
              {connected ? (
                <Button variant="outlined" color="error" onClick={handleDisconnect} disabled={loading}>
                  {t("Disconnect")}
                </Button>
              ) : (
                <Button variant="contained" onClick={handleConnect} disabled={loading}>
                  {needsReconnect ? t("Connect again") : t("Connect Furgonetka account")}
                </Button>
              )}
            </Box>
          )}
        </Box>
      )}

      {needsReconnect && account.last_error && <Alert severity="warning">{account.last_error}</Alert>}
      {connected && !account.sender_complete && (
        <Alert severity="warning">
          {t("Complete the sender details below - couriers need the address, phone number and e-mail.")}
        </Alert>
      )}

      <DetailSection title={t("Sender")} description={t("Printed on the label; the courier collects parcels here.")}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, p: 2 }}>
          {field("sender_company", t("Company"))}
          {field("sender_name", t("Contact person"))}
          {field("sender_street", t("Street"), { required: true })}
          <Box sx={{ display: "flex", gap: 1 }}>
            <Box sx={{ flex: 1 }}>{field("sender_postcode", t("Zip code"), { required: true })}</Box>
            <Box sx={{ flex: 2 }}>{field("sender_city", t("City"), { required: true })}</Box>
          </Box>
          <CountrySelect
            label={t("Country")}
            value={draft.sender_country}
            onChange={(code) => setDraft((previous) => ({ ...previous, sender_country: code }))}
            disabled={!canChange || loading}
          />
          {field("sender_phone", t("Phone number"), { required: true })}
          {field("sender_email", t("Email"), { required: true, type: "email" })}
        </Box>
      </DetailSection>

      <DetailSection title={t("New shipment defaults")}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(5, 1fr)" }, gap: 2, p: 2 }}>
          {field("default_width", t("Width (cm)"), { numeric: true })}
          {field("default_depth", t("Length (cm)"), { numeric: true })}
          {field("default_height", t("Height (cm)"), { numeric: true })}
          {field("default_weight", t("Weight (kg)"), { numeric: true })}
          <TextField
            select
            size="small"
            label={t("Label format")}
            value={draft.label_page_format ?? "a4"}
            onChange={(event) => setDraft((previous) => ({ ...previous, label_page_format: event.target.value }))}
            disabled={!canChange || loading}
          >
            <MenuItem value="a4">A4</MenuItem>
            <MenuItem value="a6">A6</MenuItem>
          </TextField>
        </Box>
      </DetailSection>

      {canChange ? (
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button variant="contained" color="success" disabled={!hasChanges || loading} onClick={handleSave}>
            {loading ? t("Saving...") : t("Save")}
          </Button>
        </Box>
      ) : (
        <Typography variant="caption" color="text.secondary">
          {t("You do not have permission to change these settings.")}
        </Typography>
      )}
    </DetailPage>
  );
};
